import { useState, useEffect } from 'preact/hooks';
import { getCreditsReceipts, type CreditReceipt } from '../api/billing';
import { PortalTaxLine } from './PortalTaxLine';

// ════════════════════════════════════════════════════════════════════════════
// PortalReceiptsTab — read-only list of paid top-up lots (valko-credits-platform
// Phase 3). Each row is a paid `topup` lot with the receipt metadata captured at
// credit time (orderNumber / receiptRef / country / stripeSessionId / currency).
//
// STRICTLY READ-ONLY: this tab NEVER exposes a refund/storno control. Refunds are
// Stripe-Refund-only, drawn solely by the CaaS refund.created webhook; storno /
// compensating rows appear in the History tab as negative entries. The portal
// issues NO POST to /v1/stornos or /v1/documents and no refund-initiating call.
//
// Read-only fetch state (UsageTab precedent), not part of the credits FSM. The
// backend read endpoint (`GET /me/credits/receipts`) may not exist yet → graceful
// "coming soon" state on error.
//
// Stripe Tax P3 (voice-assistant issue #2176): each receipt also carries the Stripe-computed tax (`r.tax`,
// read by the backend from `stripe_tax_record`), rendered by <PortalTaxLine/> under the meta rows — the
// country is already in this card's meta row, so the tax line omits it. Additive; a receipt with no `tax`
// renders exactly as before. Pinned by setpoint-evals/stripe-tax-p3/SE-02.
// ════════════════════════════════════════════════════════════════════════════

/** Fiscal EUR gross (string decimal from the ledger) → "EUR 15.00". */
function fiat(eurGross?: string | null): string | null {
  if (eurGross == null) return null;
  const n = Number(eurGross);
  if (!Number.isFinite(n)) return null;
  return `EUR ${n.toFixed(2)}`;
}

const cardStyle: Record<string, string | number> = {
  background: 'var(--bg-elevated, #1a1a2e)',
  border: '1px solid var(--border-subtle, #2a2a44)',
  borderRadius: 10,
  padding: '14px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const metaRowStyle: Record<string, string | number> = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 8,
  fontSize: 11,
  color: 'var(--text-dim, #5a5a7e)',
};

export function PortalReceiptsTab() {
  const [receipts, setReceipts] = useState<CreditReceipt[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getCreditsReceipts()
      .then((r) => {
        if (cancelled) return;
        setReceipts(r.receipts ?? []);
      })
      .catch(() => {
        if (cancelled) return;
        setUnavailable(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div class="admin-content" style={{ padding: 24, textAlign: 'center', color: 'var(--text-dim, #5a5a7e)' }}>
        Loading receipts…
      </div>
    );
  }

  if (unavailable) {
    return (
      <div class="admin-content" style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary, #8888aa)' }}>
        Receipts aren't available yet.
        <div style={{ fontSize: 12, color: 'var(--text-dim, #5a5a7e)', marginTop: 4 }}>
          Your paid top-up receipts will appear here.
        </div>
      </div>
    );
  }

  if (!receipts || receipts.length === 0) {
    return (
      <div class="admin-content" style={{ padding: 24, textAlign: 'center', color: 'var(--text-dim, #5a5a7e)' }}>
        No receipts yet.
        <div style={{ fontSize: 12, color: 'var(--text-dim, #5a5a7e)', marginTop: 4 }}>
          Purchase a prepaid pack from the Balance tab.
        </div>
      </div>
    );
  }

  return (
    <div class="admin-content" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      {receipts.map((r) => {
        const fiatStr = fiat(r.eurGross);
        return (
          <div key={r.lotId} style={cardStyle} data-testid="portal-receipt" data-order={r.orderNumber ?? ''}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary, #c8c8e8)' }}>
                {fiatStr || 'Top-up'}
              </span>
              <span style={{ fontSize: 11, color: 'var(--text-dim, #5a5a7e)', whiteSpace: 'nowrap' }}>
                {new Date(r.createdAt).toLocaleDateString()}
              </span>
            </div>
            <div style={metaRowStyle}>
              <span>{new Date(r.createdAt).toLocaleString()}</span>
              {r.country && <span>{r.country}</span>}
            </div>
            {(r.orderNumber || r.receiptRef) && (
              <div style={metaRowStyle}>
                {r.orderNumber && <span>Order {r.orderNumber}</span>}
                {r.receiptRef && <span>Ref {r.receiptRef}</span>}
              </div>
            )}
            <PortalTaxLine tax={r.tax} showCountry={false} />
          </div>
        );
      })}
    </div>
  );
}
